import { isApiError } from "@sagot/api-client";
import { consoleRoutes, webappAgentUrl } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Segmented, Tabs, TabsContent, TabsList, TabsTrigger, toast } from "@sagot/ui";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useResetWebappDesign, useSaveWebappDesign } from "./use-webapp-studio";
import { BrandingPanel, ContentPanel, IconsPanel, LayoutPanel, SharePanel, type PanelProps } from "./webapp-panels";
import { WebappPreview, type PreviewDevice, type PreviewTheme } from "./webapp-preview";
import { DEFAULT_SETTINGS, WEBAPP_TABS, fingerprint, isWebappTab, sameDesign, toBody, type WebappScope, type WebappStudioOut, type WebappTab } from "./webapp-model";

const K = "webappStudio.console";
const FLUSH = "-mx-4 -my-6 lg:-mx-8 lg:-my-8";
const TITLE_ID = "webapp-studio-title";

interface WebappEditorProps {
  agentId: string;
  agentName: string;
  design: WebappStudioOut;
}

const PANELS: Record<WebappTab, (props: PanelProps) => React.ReactElement> = {
  branding: BrandingPanel,
  icons: IconsPanel,
  content: ContentPanel,
  layout: LayoutPanel,
  share: SharePanel,
};

function errorText(error: unknown, fallback: string): string {
  return isApiError(error) && error.message ? error.message : fallback;
}

export function WebappEditor({ agentId, agentName, design }: WebappEditorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const param = useParams().tab;
  const tab: WebappTab = isWebappTab(param) ? param : "branding";
  const permissions = usePermissions(agentId);
  const canEdit = permissions.can("widget.manage", { agentId }) && !permissions.readOnly;
  const [scope, setScope] = useState<WebappScope>("assistant");
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [theme, setTheme] = useState<PreviewTheme>("light");
  const save = useSaveWebappDesign(agentId);
  const reset = useResetWebappDesign(agentId);

  const saved = scope === "default" ? (design.default_settings ?? DEFAULT_SETTINGS) : design.settings;
  const savedKey = `${scope}:${fingerprint(saved)}`;
  const [draft, setDraft] = useState(saved);
  const [seen, setSeen] = useState(savedKey);
  if (seen !== savedKey) {
    // The saved design changed (a save, a reset, the other scope): the form follows it.
    setSeen(savedKey);
    setDraft(saved);
  }
  const dirty = !sameDesign(draft, saved);
  const hasOwn = scope === "assistant" ? design.source === "assistant" : design.default_settings !== null;
  const Panel = PANELS[tab];

  const onSave = () => {
    save.mutate(
      { scope, settings: toBody(scope, draft).settings },
      {
        onSuccess: () => toast.success(t(`${K}.saved`)),
        onError: (error) => toast.error(errorText(error, t(`${K}.saveFailed`))),
      },
    );
  };
  const onReset = () => {
    reset.mutate(scope, {
      onSuccess: () => toast.success(t(`${K}.resetDone`)),
      onError: (error) => toast.error(errorText(error, t(`${K}.resetFailed`))),
    });
  };

  return (
    <div className={`${FLUSH} flex flex-col lg:h-[calc(100vh-3.5rem)] lg:flex-row`}>
      <aside className="border-b border-line bg-surface lg:w-[420px] lg:shrink-0 lg:overflow-y-auto lg:border-b-0 lg:border-r" aria-labelledby={TITLE_ID}>
        <Tabs value={tab} onValueChange={(next) => void navigate(consoleRoutes.webappStudio(summary.slug, agentId, next), { replace: true })}>
          <div className="sticky top-0 z-10 border-b border-line bg-surface px-5 pt-5">
            <h1 id={TITLE_ID} tabIndex={-1} className="text-2xl font-extrabold tracking-tight outline-none">
              {t(`${K}.title`)}
            </h1>
            <p className="mt-1 text-fg-muted">{t(`${K}.subtitle`, { agent: agentName })}</p>
            <TabsList className="-mx-1 mt-4 flex-wrap border-b-0" aria-label={t(`${K}.tabsLabel`)}>
              {WEBAPP_TABS.map((value) => (
                <TabsTrigger key={value} value={value} className="shrink-0 whitespace-nowrap px-2.5 text-[13px]">
                  {t(`${K}.tabs.${value}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="space-y-5 p-5">
            <div>
              <span className="label">{t(`${K}.scope.label`)}</span>
              <Segmented
                aria-label={t(`${K}.scope.label`)}
                value={scope}
                onValueChange={(next) => {
                  if (!dirty) setScope(next);
                }}
                options={[
                  { value: "assistant", label: t(`${K}.scope.assistant`) },
                  { value: "default", label: t(`${K}.scope.default`) },
                ]}
              />
              <p className="hint" data-testid="scope-hint">
                {dirty ? t(`${K}.scope.locked`) : scope === "assistant" ? t(`${K}.scope.assistantHint`, { agent: agentName }) : t(`${K}.scope.defaultHint`)}
              </p>
              <p className="mt-2 flex items-start gap-1.5 text-xs text-fg-subtle" data-testid="design-source">
                <Icon name="info" className="mt-0.5 size-3.5 shrink-0" />
                {t(`${K}.source.${design.source}`, { agent: agentName })}
              </p>
            </div>
            {canEdit ? null : <p className="hint">{t(`${K}.readOnly`)}</p>}
            <fieldset disabled={!canEdit} className="min-w-0">
              {WEBAPP_TABS.map((value) => (
                <TabsContent key={value} value={value} forceMount className="data-[state=inactive]:hidden">
                  {value === tab ? <Panel settings={draft} onChange={setDraft} design={design} disabled={!canEdit} isDefault={scope === "default"} agentId={agentId} /> : null}
                </TabsContent>
              ))}
            </fieldset>
            {canEdit && hasOwn ? (
              <Button variant="ghost" size="sm" loading={reset.isPending} onClick={onReset}>
                <Icon name="rotate-ccw" />
                {scope === "default" ? t(`${K}.resetDefault`) : t(`${K}.reset`)}
              </Button>
            ) : null}
          </div>
        </Tabs>
      </aside>
      <section className="min-w-0 flex-1 bg-canvas p-4 lg:overflow-y-auto lg:p-8" aria-labelledby="webapp-preview-title">
        <div className="mx-auto max-w-5xl">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 id="webapp-preview-title" className="flex items-center gap-2 text-sm font-semibold">
              <span aria-hidden="true" className="size-2 rounded-pill bg-success" />
              {t(`${K}.preview.live`)} <span className="font-normal text-fg-subtle">{t(`${K}.preview.liveHint`)}</span>
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              {dirty ? (
                <Badge tone="warning" data-testid="unsaved">
                  <Icon name="alert-circle" className="size-3" />
                  {t(`${K}.preview.unsaved`)}
                </Badge>
              ) : null}
              <Segmented
                aria-label={t(`${K}.preview.device`)}
                value={device}
                onValueChange={setDevice}
                options={[
                  { value: "desktop", label: t(`${K}.preview.desktop`) },
                  { value: "mobile", label: t(`${K}.preview.mobile`) },
                ]}
              />
              <Segmented
                aria-label={t(`${K}.preview.theme`)}
                value={theme}
                onValueChange={setTheme}
                options={[
                  { value: "light", label: t(`${K}.preview.light`) },
                  { value: "dark", label: t(`${K}.preview.dark`) },
                ]}
              />
              <Button asChild variant="secondary" size="sm">
                <a href={webappAgentUrl(summary.slug, design.agent_key)} target="_blank" rel="noopener noreferrer" aria-label={t(`${K}.preview.openLabel`)}>
                  <Icon name="external-link" />
                  {t(`${K}.preview.open`)}
                </a>
              </Button>
              {canEdit ? (
                <>
                  <Button variant="secondary" size="sm" disabled={!dirty} onClick={() => { setDraft(saved); }}>
                    {t(`${K}.discard`)}
                  </Button>
                  <Button size="sm" disabled={!dirty} loading={save.isPending} onClick={onSave}>
                    {save.isPending ? t(`${K}.saving`) : t(`${K}.save`)}
                  </Button>
                </>
              ) : null}
            </div>
          </div>
          <WebappPreview settings={draft} design={design} agentName={agentName} device={device} theme={theme} />
        </div>
      </section>
    </div>
  );
}
